import { useCallback, useMemo, useState } from 'react';

import { addDays, isWithinInterval, setHours, setMinutes, setSeconds } from 'date-fns';
import { isEqual } from 'lodash-es';
import { compose } from 'utils';

import { IDayPart, Maybe } from 'generated/sanity-graphql';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useInterval from 'hooks/use-interval';
import logger from 'utils/logger';

export interface IDayPartBoundary {
  key: string;
  startTime: Date;
  endTime: Date;
}

type IFeatureMenuDayPart = Omit<IDayPart, 'displayName'> & {
  displayName: Maybe<{ locale: Maybe<string> }>;
};

export interface IValidDayPart extends IFeatureMenuDayPart {
  endTime: string;
  key: string;
  startTime: string;
}

export const isValidDayPart = (maybeDayPart: IFeatureMenuDayPart): maybeDayPart is IValidDayPart =>
  !!maybeDayPart && !!maybeDayPart.key && !!maybeDayPart.endTime && !!maybeDayPart.startTime;

// returns a function that converts
// a Date object to the given hour
// and minute on the same day
const setTime = (hour: number, minute: number) =>
  compose<[Date], Date, Date, Date>(
    date => setHours(date, hour),
    date => setMinutes(date, minute),
    date => setSeconds(date, 0)
  );

const transformDateWithTimeString = (timeString: string): Date => {
  const now = new Date();
  const time = timeString.split(':').map(t => parseInt(t, 10));

  if (time.length !== 2 || time.some(t => isNaN(t))) {
    logger.error({ message: 'Received invalid daypart time-string', timeString });
    return now;
  }

  const [hours, minutes] = time;

  return setTime(hours, minutes)(now);
};

function dayPartTimeStringToTodayTime(timeString: string, tomorrow?: boolean): Date {
  const transformedDate = transformDateWithTimeString(timeString);
  if (tomorrow) {
    return addDays(transformedDate, 1);
  }

  return transformedDate;
}

// given a mapping of daypart to time-string (HH:MM),
// returns a function that accepts the local time and
// returns the daypart name(s) that are currently "active"
const computeActiveDayParts = (dayParts: ReadonlyArray<IValidDayPart>) => {
  // normally we'd just map but the conditional serves a type check that all needed values are present
  const dayPartBoundaries = dayParts.map<IDayPartBoundary>(
    ({ endTime, key, startTime }) => ({
      endTime: dayPartTimeStringToTodayTime(endTime, startTime > endTime),
      key,
      startTime: dayPartTimeStringToTodayTime(startTime),
    }),
    []
  );

  return (now: Date): IDayPartBoundary[] => {
    if (!dayPartBoundaries.length) {
      return [];
    }
    try {
      return dayPartBoundaries.filter(({ endTime, startTime }: IDayPartBoundary) =>
        isWithinInterval(now, { start: startTime, end: endTime })
      );
    } catch (error) {
      logger.error({ dayParts, error, message: 'Error finding active day-parts' });
      return [];
    }
  };
};

// 60 (s) * 1000 (ms)
export const REFRESH_INTERVAL = 60 * 1000;

/**
 * returns the list of currently active dayparts
 * out of the given dayParts array
 */
export default function useActiveDayParts({
  dayParts,
}: {
  dayParts: ReadonlyArray<IValidDayPart>;
}): IDayPartBoundary[] {
  const computeActive = useMemo(() => computeActiveDayParts(dayParts), [dayParts]);

  const [activeDayParts, setActiveDayParts] = useState<IDayPartBoundary[]>(
    computeActive(new Date())
  );

  const setActiveDayPartsIfChanged = useCallback((newActiveDayParts: IDayPartBoundary[]) => {
    setActiveDayParts(currentlyActiveDayParts =>
      !isEqual(currentlyActiveDayParts, newActiveDayParts)
        ? newActiveDayParts
        : currentlyActiveDayParts
    );
  }, []);

  // refresh active dayparts every 60s
  useInterval(() => {
    setActiveDayPartsIfChanged(computeActive(new Date()));
  }, REFRESH_INTERVAL);

  // set active dayparts on dayparts change
  useEffectOnUpdates(() => {
    setActiveDayPartsIfChanged(computeActive(new Date()));
  }, [dayParts, setActiveDayPartsIfChanged]);

  return activeDayParts;
}
